import { Address, WalletKey } from "@silo/types";
import { cn } from "../../utils/class-names";
import { match } from "ts-pattern";
import { tv } from "tailwind-variants";
import { formatAddress } from "@silo/utils";
import { AlertIcon, CheckmarkIcon, LoaderIcon, MaskIcon } from "../../icons";
import { Avatar } from "./avatar";

const accountBadge = tv({
  slots: {
    badge: "bg-shale-850 rounded-md px-2.5 py-[7px] text-shale-200 font-medium",
    wrapper: "flex items-center justify-center gap-2",
    icon: "w-5 h-5",
  },
});

const { badge, wrapper, icon } = accountBadge();

export interface AccountBadgeProps {
  className?: string;
  account: Address;
  ensName: string | undefined;
  state: AccountBadgeState;
  isImpersonating: boolean;
  walletKey: WalletKey;
}

export type AccountBadgeState =
  | { _tag: "idle" }
  | { _tag: "pending"; count: number }
  | { _tag: "success" }
  | { _tag: "error"; message?: string };

export function AccountBadge({
  className,
  state,
  ensName,
  account,
  isImpersonating,
  walletKey,
}: AccountBadgeProps): JSX.Element {
  return (
    <div className={cn("min-w-[120px]", className)}>
      {match(state)
        .with({ _tag: "idle" }, () => (
          <div
            className={badge({
              className:
                "group-hover:bg-shale-800 hover:ease-in duration-100 group-active:bg-shale-850",
            })}
          >
            <div className={wrapper()}>
              <div className="overflow-hidden overflow-ellipsis max-w-[150px]">
                {ensName ?? formatAddress(account)}
              </div>

              {isImpersonating ? (
                <MaskIcon className={icon({ className: "text-amber-300" })} />
              ) : (
                <Avatar account={account} walletKey={walletKey} />
              )}
            </div>
          </div>
        ))
        .with({ _tag: "pending" }, ({ count }) => (
          <div className={badge()}>
            <div className={wrapper()}>
              <div>{count} Pending</div>

              <LoaderIcon className={icon({ className: "animate-spin" })} />
            </div>
          </div>
        ))
        .with({ _tag: "success" }, () => (
          <div
            className={badge({ className: "bg-green-500" })}
            data-cy="transaction-success"
          >
            <div className={wrapper({ className: "text-white" })}>
              <div>Success</div>

              <CheckmarkIcon className={icon()} />
            </div>
          </div>
        ))
        .with({ _tag: "error" }, ({ message = "Error" }) => (
          <div className={badge({ className: "bg-red-500" })}>
            <div className={wrapper({ className: "text-white" })}>
              <div>{message}</div>

              <AlertIcon className={icon()} />
            </div>
          </div>
        ))
        .exhaustive()}
    </div>
  );
}
